import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Coins, Megaphone, X } from "lucide-react";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { cn } from "@/lib/utils";

// One-time site-wide "ways to earn" announcement: a two-slide carousel for the
// affiliate program (cash for referrals) and share-to-earn rewards (credits
// for views). Any visitor (signed in or not) sees it once per browser; the
// localStorage flag — not a time window — is what prevents repeats. Bump the
// key suffix to re-announce.
const SEEN_KEY = "solmi_affiliate_announcement_seen_v1";
// Let the page settle before popping so it doesn't compete with initial load.
const SHOW_DELAY_MS = 2500;
const AFFILIATE_PATH = "/affiliate-program";
const REWARDS_PATH = "/rewards-help";

export default function AffiliateAnnouncement() {
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const { withLanguagePath } = useLanguageRouter();

  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    // Pointless on the pages it advertises; the flag stays unset so the
    // visitor is still greeted on a later visit elsewhere.
    if (location.includes(AFFILIATE_PATH) || location.includes(REWARDS_PATH))
      return;

    try {
      if (localStorage.getItem(SEEN_KEY) === "1") return;
    } catch {
      return; // No storage → can't guarantee once-only, so don't show.
    }

    const timer = setTimeout(() => {
      try {
        localStorage.setItem(SEEN_KEY, "1");
      } catch {
        return;
      }
      setVisible(true);
      requestAnimationFrame(() => setEntered(true));
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
    // Decide once per mount; ignore later route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = useCallback(() => {
    setEntered(false);
    window.setTimeout(() => setVisible(false), 200);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, close]);

  if (!visible) return null;

  const goTo = (path: string) => {
    navigate(withLanguagePath(path));
    close();
  };

  const slides = [
    {
      Icon: Megaphone,
      title: t("affiliateAnnouncement.affiliate.title", {
        defaultValue: "Introducing the Solmi Affiliate Program",
      }),
      body: t("affiliateAnnouncement.affiliate.body", {
        defaultValue:
          "Earn 20% of the first purchase from every creator you refer — subscription or credit pack. Free to join, paid monthly.",
      }),
      cta: t("affiliateAnnouncement.affiliate.cta", {
        defaultValue: "Learn More",
      }),
      path: AFFILIATE_PATH,
    },
    {
      Icon: Coins,
      title: t("affiliateAnnouncement.rewards.title", {
        defaultValue: "Share to Earn: free credits for views",
      }),
      body: t("affiliateAnnouncement.rewards.body", {
        defaultValue:
          "Post your Solmi videos on YouTube, TikTok, Instagram, or X and earn free credits as they rack up views — over 92,000 credits at 1M views.",
      }),
      cta: t("affiliateAnnouncement.rewards.cta", {
        defaultValue: "See How It Works",
      }),
      path: REWARDS_PATH,
    },
  ];
  const isLast = slide === slides.length - 1;
  const current = slides[slide];

  return (
    <div
      className={cn(
        "fixed inset-0 z-[80] flex items-center justify-center p-4",
        "transition-opacity duration-200",
        entered ? "opacity-100" : "opacity-0",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="affiliate-announcement-title"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      <div
        className={cn(
          "relative w-full max-w-md rounded-2xl border border-white/10 bg-[#202029] p-6 text-white shadow-2xl",
          "transition-transform duration-200 ease-out",
          entered ? "translate-y-0 scale-100" : "translate-y-2 scale-95",
        )}
      >
        <button
          type="button"
          onClick={close}
          aria-label={t("affiliateAnnouncement.dismiss", {
            defaultValue: "Dismiss",
          })}
          className="absolute right-3 top-3 rounded-md p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {/* Fixed content height so the card doesn't jump between slides. */}
        <div key={slide} className="min-h-[148px] animate-in fade-in duration-200">
          <div className="flex items-center gap-3 pr-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500/40 to-purple-500/40 ring-1 ring-inset ring-white/10">
              <current.Icon
                className="h-5 w-5 text-indigo-200"
                aria-hidden="true"
              />
            </span>
            <h3
              id="affiliate-announcement-title"
              className="text-lg font-semibold leading-tight"
            >
              {current.title}
            </h3>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-white/75">
            {current.body}
          </p>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => goTo(current.path)}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
          >
            {current.cta}
          </button>
          {isLast ? (
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white/60 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              {t("affiliateAnnouncement.later", { defaultValue: "Maybe Later" })}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setSlide((s) => s + 1)}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white/60 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
            >
              {t("affiliateAnnouncement.next", { defaultValue: "Next" })}
            </button>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.path}
                type="button"
                onClick={() => setSlide(i)}
                aria-label={t("affiliateAnnouncement.goToSlide", {
                  index: i + 1,
                  defaultValue: "Go to slide {{index}}",
                })}
                aria-current={i === slide}
                className={cn(
                  "h-2 rounded-full transition-all",
                  i === slide
                    ? "w-5 bg-indigo-400"
                    : "w-2 bg-white/25 hover:bg-white/40",
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
